import { useState, FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import Modal from './Modal';
import { vendorApi } from '../services/vendor';
import extractApiError from '../utils/extractApiError';

interface SuggestContextChangeModalProps {
  siteId: string;
  siteName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function SuggestContextChangeModal({
  siteId,
  siteName,
  isOpen,
  onClose,
}: SuggestContextChangeModalProps) {
  const [suggestion, setSuggestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: () => vendorApi.suggestSiteContextChange(siteId, suggestion.trim()),
    onSuccess: () => {
      setSuccess(true);
      setSuggestion('');
      setError(null);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    },
    onError: (err: unknown) => setError(extractApiError(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!suggestion.trim()) {
      setError('Please enter a suggestion.');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Suggest a Site Context change — ${siteName}`}>
      {success ? (
        <div className="text-center py-6">
          <p className="text-emerald-600 font-medium">Suggestion submitted.</p>
          <p className="text-xs text-slate-500 mt-1">The client has been notified.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-600">
            Describe what should change in this site's context (e.g. the site type is wrong,
            patrol intensity should be higher, etc.). The client will see your suggestion in
            their notifications.
          </p>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              What should change and why?
            </label>
            <textarea
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              rows={5}
              maxLength={2000}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder="The site is actually a warehouse, not an office..."
            />
            <p className="mt-1 text-xs text-slate-400 text-right">{suggestion.length} / 2000</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !suggestion.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50"
            >
              {mutation.isPending ? 'Submitting...' : 'Submit Suggestion'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
